import { useState } from 'react';
import type { DisambiguationCandidate } from '@/lib/api';
import { InteractiveTable, type InteractiveTableColumn } from './InteractiveTable';

interface DisambiguationTableProps {
  candidates: DisambiguationCandidate[];
  /** Called with the chosen candidate when a row is selected. */
  onSelect: (candidate: DisambiguationCandidate) => void;
  /**
   * clientId of the already-chosen candidate, or null/undefined when none has
   * been selected. When set, the table renders its resolved state (chosen row
   * bold + aria-selected, other rows non-selectable). On session restore this
   * is inferred from the pinned active client id — no separate persisted field.
   */
  selectedClientId?: string | null;
}

export function candidateName(c: DisambiguationCandidate): string {
  const name = [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
  if (name && c.preferredName && c.preferredName !== c.firstName) {
    return `${name} (${c.preferredName})`;
  }
  return name || c.preferredName || 'Unknown client';
}

/**
 * Formats a candidate's location as "Suburb, State" from `preferredSuburb` /
 * `preferredState`. Either part may be absent: with only one present it renders
 * that part alone; with neither it returns an empty string (the caller shows
 * the "Not recorded" fallback). Location is shown in the pick-list in place of
 * date of birth (DOB is still returned by the backend but no longer displayed).
 */
export function candidateLocation(c: DisambiguationCandidate): string {
  return [c.preferredSuburb, c.preferredState].filter(Boolean).join(', ').trim();
}

/**
 * Builds the copyable text for a disambiguation turn: the response message
 * followed by a Markdown table of the candidates that mirrors what
 * DisambiguationTable renders on screen.
 *
 * The disambiguation table is a structured React component (built from
 * `candidates`), NOT part of `response` markdown, so a copy of `response` alone
 * would miss the table. This serialises the candidates so the clipboard content
 * matches the visible message + table — consistent with normal data tables,
 * whose markdown table already lives inside `response`.
 *
 * Columns where ALL candidates have empty values are omitted from the copy
 * text to match the visual table. When columns are omitted, a note is appended.
 */
export function disambiguationCopyText(
  response: string,
  candidates: DisambiguationCandidate[],
): string {
  if (candidates.length === 0) return response;

  // Determine which optional columns have data (match the visual table filtering)
  const hasLocation = candidates.some((c) => candidateLocation(c));
  const hasAdviser = candidates.some((c) => c.clientAdviserName);

  // Build header and rows based on visible columns
  const headerParts = ['Client name'];
  if (hasLocation) headerParts.push('Location');
  if (hasAdviser) headerParts.push('Adviser');
  const header = `| ${headerParts.join(' | ')} |`;
  const divider = `| ${headerParts.map(() => '---').join(' | ')} |`;

  const rows = candidates.map((c) => {
    const parts = [candidateName(c)];
    if (hasLocation) parts.push(candidateLocation(c) || 'Not recorded');
    if (hasAdviser) parts.push(c.clientAdviserName || 'Not recorded');
    return `| ${parts.join(' | ')} |`;
  });
  const table = [header, divider, ...rows].join('\n');

  // Build the hidden columns note
  const hiddenColumns: string[] = [];
  if (!hasLocation) hiddenColumns.push('Location');
  if (!hasAdviser) hiddenColumns.push('Adviser');
  const hiddenNote =
    hiddenColumns.length > 0
      ? `\n\nSome information was not available: ${hiddenColumns.join(', ')}.`
      : '';

  const baseText = response.trim() ? `${response.trim()}\n\n${table}` : table;
  return baseText + hiddenNote;
}

/** Column definitions for the disambiguation pick-list. */
const columns: InteractiveTableColumn<DisambiguationCandidate>[] = [
  {
    header: 'Client name',
    cell: (c) => candidateName(c),
    // Client name is never hideable — always shown as the identifying column
  },
  {
    header: 'Location',
    cell: (c) => candidateLocation(c) || 'Not recorded',
    cellClassName: (c) => (candidateLocation(c) ? undefined : 'not-recorded'),
    hideable: true,
    isEmpty: (c) => !candidateLocation(c),
  },
  {
    header: 'Adviser',
    cell: (c) => c.clientAdviserName || 'Not recorded',
    cellClassName: (c) => (c.clientAdviserName ? undefined : 'not-recorded'),
    hideable: true,
    isEmpty: (c) => !c.clientAdviserName,
  },
];

/**
 * Renders the 2–5 same-named client candidates as a selectable pick-list.
 *
 * This is a thin wrapper over the generic InteractiveTable: it maps candidates
 * to the Client name / Location / Adviser columns. All interactive-table
 * styling (plain rows, hover/focus highlight, keyboard selection) lives in
 * InteractiveTable; this component only owns the disambiguation column mapping.
 *
 * Columns where ALL candidates have missing data (e.g. all advisers are empty)
 * are automatically hidden, and an informational message is displayed below
 * the table listing the unavailable information.
 *
 * Selecting a row hands the chosen candidate back to the caller (which sets the
 * active client context and re-populates the original message — Model X). The
 * table stays rendered as a transcript entry after selection; it does not block
 * further input.
 */
export function DisambiguationTable({
  candidates,
  onSelect,
  selectedClientId,
}: Readonly<DisambiguationTableProps>) {
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  return (
    <>
      <InteractiveTable
        className="porter-disambiguation"
        columns={columns}
        rows={candidates}
        rowKey={(c) => c.clientId}
        onSelect={onSelect}
        rowAriaLabel={(c) => `Select ${candidateName(c)}`}
        selectedRowKey={selectedClientId}
        onColumnsHidden={setHiddenColumns}
      />
      {hiddenColumns.length > 0 && (
        <p className="porter-disambiguation__hidden-info">
          Some information was not available: {hiddenColumns.join(', ')}.
        </p>
      )}
    </>
  );
}
